import { Box, Stack } from "@mui/material";
import {
  Button,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface ChatItemAction {
  label: string;
  name: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ChatItemContainerProps {
  alignment?: "incoming" | "outgoing";
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  primaryAction?: ChatItemAction;
  secondaryAction?: ChatItemAction;
  actions?: ChatItemAction[];
  ariaLabel?: string;
  bodyScrollMode?: "dynamic" | "always";
  flushBodyBottom?: boolean;
}

const MAX_CHAT_ITEM_WIDTH = "800px";
const SCROLL_FADE_HEIGHT = "64px";
const SCROLL_FADE_OVERLAP = "3px";
const SCROLL_EDGE_TOLERANCE = 1;
const CHAT_ITEM_MAX_VIEWPORT_HEIGHT =
  "calc(100dvh - var(--chat-header-height, 72px) - var(--chat-timeline-vertical-gutter, 16px) - var(--chat-timeline-vertical-gutter, 16px))";
const CHAT_ITEM_MAX_CONTAINER_HEIGHT =
  "calc(100cqh - var(--chat-header-height, 72px) - var(--chat-timeline-vertical-gutter, 16px) - var(--chat-timeline-vertical-gutter, 16px))";

interface ChatBodyScrollState {
  isScrollable: boolean;
  isAtTop: boolean;
  isAtBottom: boolean;
}

/**
 * The common layout shell for every item in the chat timeline.
 *
 * Question-specific controls are deliberately supplied as children. This
 * component owns only chat alignment, sizing, scrolling and sticky content.
 */
export const ChatItemContainer = ({
  alignment = "incoming",
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  actions,
  ariaLabel,
  bodyScrollMode = "dynamic",
  flushBodyBottom = false,
}: ChatItemContainerProps): React.ReactElement => {
  const tokens = useFoundationColorTokens();
  const spacing = useSpacing();
  const typography = useTypography();
  const isOutgoing = alignment === "outgoing";
  const answerBackground = tokens.backgroundInverse;
  const cardBackground = isOutgoing
    ? answerBackground
    : tokens.backgroundElevatedLevel1;
  const answerTextColor = tokens.labelOnSurface;
  const hasHeader = title !== undefined || description !== undefined;
  const hasBody =
    children !== undefined && children !== null && children !== false;
  const itemActions =
    actions ??
    [secondaryAction, primaryAction].filter(
      (action): action is ChatItemAction => action !== undefined,
    );
  const hasActions = itemActions.length > 0;
  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyContentRef = useRef<HTMLDivElement>(null);
  const [bodyScrollState, setBodyScrollState] = useState<ChatBodyScrollState>({
    isScrollable: false,
    isAtTop: true,
    isAtBottom: true,
  });

  const updateBodyScrollState = useCallback((): void => {
    const body = bodyRef.current;
    if (!body) return;

    const isScrollable =
      body.scrollHeight > body.clientHeight + SCROLL_EDGE_TOLERANCE;
    const nextBodyScrollState = {
      isScrollable,
      isAtTop: !isScrollable || body.scrollTop <= SCROLL_EDGE_TOLERANCE,
      isAtBottom:
        !isScrollable ||
        body.scrollTop + body.clientHeight >=
          body.scrollHeight - SCROLL_EDGE_TOLERANCE,
    };

    setBodyScrollState((currentBodyScrollState) => {
      if (
        currentBodyScrollState.isScrollable ===
          nextBodyScrollState.isScrollable &&
        currentBodyScrollState.isAtTop === nextBodyScrollState.isAtTop &&
        currentBodyScrollState.isAtBottom === nextBodyScrollState.isAtBottom
      ) {
        return currentBodyScrollState;
      }

      return nextBodyScrollState;
    });
  }, []);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    const bodyContent = bodyContentRef.current;

    if (!body || !bodyContent) return undefined;

    updateBodyScrollState();

    const resizeObserver = new ResizeObserver(updateBodyScrollState);
    resizeObserver.observe(body);
    resizeObserver.observe(bodyContent);
    window.addEventListener("resize", updateBodyScrollState);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateBodyScrollState);
    };
  }, [children, hasBody, updateBodyScrollState]);

  const shouldShowScrollFades = hasActions && bodyScrollState.isScrollable;
  const shouldEnableBodyScroll =
    bodyScrollMode === "always" || bodyScrollState.isScrollable;

  return (
    <Stack
      component="article"
      aria-label={ariaLabel}
      alignSelf={isOutgoing ? "flex-end" : "flex-start"}
      width={isOutgoing ? "fit-content" : "calc(100% - 32px)"}
      maxWidth={
        isOutgoing
          ? `min(${MAX_CHAT_ITEM_WIDTH}, calc(100% - 32px))`
          : MAX_CHAT_ITEM_WIDTH
      }
      bgcolor={cardBackground}
      sx={{
        borderRadius: isOutgoing ? "20px 0 20px 20px" : "0 20px 20px 20px",
        overflow: "hidden",
        ...(!isOutgoing
          ? {
              maxHeight: CHAT_ITEM_MAX_VIEWPORT_HEIGHT,
              "@supports (height: 100cqh)": {
                maxHeight: CHAT_ITEM_MAX_CONTAINER_HEIGHT,
              },
            }
          : {}),
        "@media (min-width:600px)": {
          width: isOutgoing ? "fit-content" : "82%",
        },
        ...(isOutgoing
          ? {
              color: answerTextColor,
              ...typography.b1,
              "& .MuiTypography-root, & .ProseMirror": {
                color: `${answerTextColor} !important`,
                fontFamily: typography.b1.fontFamily,
                fontSize: `${typography.b1.fontSize} !important`,
              },
            }
          : {}),
      }}
    >
      {hasHeader && (
        <Stack
          flex="0 0 auto"
          gap={spacing.spaceXS}
          padding={`${spacing.spaceMD} ${spacing.spaceMD} ${
            hasBody || hasActions ? spacing.spaceSM : spacing.spaceMD
          }`}
        >
          {title}
          {description}
        </Stack>
      )}

      {hasBody && (
        <Stack
          minHeight={0}
          flex="1 1 auto"
          sx={{
            position: "relative",
          }}
        >
          <Stack
            ref={bodyRef}
            data-chat-item-body
            minHeight={0}
            flex="1 1 auto"
            padding={spacing.spaceMD}
            paddingBottom={flushBodyBottom ? 0 : spacing.spaceMD}
            onScroll={updateBodyScrollState}
            sx={{
              overflowX: "hidden",
              overflowY: shouldEnableBodyScroll ? "auto" : "visible",
              overscrollBehavior: shouldEnableBodyScroll ? "contain" : "auto",
              ...(bodyScrollState.isScrollable && {
                "@media (max-width:599.95px)": {
                  overscrollBehaviorY: "auto",
                },
              }),
            }}
          >
            <div ref={bodyContentRef}>{children}</div>
          </Stack>

          {shouldShowScrollFades && !bodyScrollState.isAtTop && (
            <Box
              aria-hidden="true"
              height={`calc(${SCROLL_FADE_HEIGHT} + ${SCROLL_FADE_OVERLAP})`}
              position="absolute"
              top={`-${SCROLL_FADE_OVERLAP}`}
              left={0}
              right={0}
              zIndex={1}
              sx={{
                pointerEvents: "none",
                background: `linear-gradient(to bottom, ${cardBackground} 0, ${cardBackground} ${SCROLL_FADE_OVERLAP}, transparent 100%)`,
              }}
            />
          )}
          {shouldShowScrollFades && !bodyScrollState.isAtBottom && (
            <Box
              aria-hidden="true"
              height={`calc(${SCROLL_FADE_HEIGHT} + ${SCROLL_FADE_OVERLAP})`}
              position="absolute"
              bottom={`-${SCROLL_FADE_OVERLAP}`}
              left={0}
              right={0}
              zIndex={1}
              sx={{
                pointerEvents: "none",
                background: `linear-gradient(to top, ${cardBackground} 0, ${cardBackground} ${SCROLL_FADE_OVERLAP}, transparent 100%)`,
              }}
            />
          )}
        </Stack>
      )}

      {hasActions && (
        <Stack
          direction="row"
          flex="0 0 auto"
          justifyContent="flex-end"
          gap={spacing.spaceSM}
          padding={
            hasBody
              ? `${spacing.spaceSM} ${spacing.spaceMD} ${spacing.spaceMD}`
              : spacing.spaceMD
          }
          bgcolor={cardBackground}
        >
          {itemActions.map((action, index) => (
            <Button
              key={action.name}
              label={action.label}
              name={action.name}
              variant={
                index === itemActions.length - 1 ? "filled" : "outlined-neutral"
              }
              color={index === itemActions.length - 1 ? "primary" : undefined}
              size="medium"
              disabled={action.disabled}
              onClick={action.onClick}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
